import React, { useState, useEffect, useRef } from 'react';
import ApiClient from '../../../methods/api/apiClient';
import loader from '../../../methods/loader';
import './style.scss';
import Html, { INDUSTRY_OPTIONS } from './Html';
import Spotlight from '../Spotlight';
import { useNavigate, useParams } from 'react-router-dom';
import formModel from '../../../models/form.model';
import { useDispatch, useSelector } from 'react-redux';
import { login_success } from '../../../Pages/actions/user';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';

// cw: mapping between a profileData object (+ bio) and the pd_* form fields. Used both for the
// global default profile and for per-group overrides, so one field set edits either context.
const csv = (a: any[] = []) => (Array.isArray(a) ? a.join(', ') : (a || ''));
const arr = (s: string) => (s || '').split(',').map((x: string) => x.trim()).filter(Boolean);

export const pdFieldsFromProfile = (pd: any = {}, bio = '') => ({
  aboutUs: bio || pd.bio || '',
  pd_headline: pd.headline || '',
  pd_industry: pd.industry || '',
  pd_specialties: csv(pd.specialties),
  pd_servicesOffered: csv(pd.servicesOffered),
  pd_servicesNeeded: csv(pd.servicesNeeded),
  pd_callMeWhen: pd.callMeWhen || '',
  pd_painPoint: pd.icp?.painPoint || '',
  pd_geography: pd.icp?.geography || '',
  pd_revenueMin: pd.icp?.revenueMin || '',
  pd_revenueMax: pd.icp?.revenueMax || '',
  pd_employeeCount: pd.icp?.employeeCount || '',
  pd_seats: pd.icp?.seats || '',
  pd_businessType: pd.businessType || '',
  pd_businessTypeOther: pd.businessTypeOther || '',
  pd_industriesServed: (pd.industriesServed || []).filter((x: string) => INDUSTRY_OPTIONS.includes(x)),
  pd_industryOther: (pd.industriesServed || []).filter((x: string) => !INDUSTRY_OPTIONS.includes(x)).join(', '),
  pd_websiteUrl: pd.websiteUrl || '',
  pd_yearsExperience: pd.yearsExperience || '',
  pd_referralFee: pd.referralFee || '',
  pd_promotions: pd.promotions || '',
  pd_preferredContact: pd.preferredContact || '',
});

export const profileFromPdFields = (form: any) => ({
  headline: form.pd_headline || '',
  bio: form.aboutUs || '',
  industry: form.pd_industry || '',
  specialties: arr(form.pd_specialties),
  servicesOffered: arr(form.pd_servicesOffered),
  servicesNeeded: arr(form.pd_servicesNeeded),
  callMeWhen: form.pd_callMeWhen || '',
  icp: {
    painPoint: form.pd_painPoint || '',
    geography: form.pd_geography || '',
    revenueMin: form.pd_revenueMin || '',
    revenueMax: form.pd_revenueMax || '',
    employeeCount: form.pd_employeeCount || '',
    seats: form.pd_seats || '',
  },
  businessType: form.pd_businessType || '',
  businessTypeOther: form.pd_businessType === 'Other' ? (form.pd_businessTypeOther || '') : '',
  industriesServed: [
    ...(form.pd_industriesServed || []),
    ...arr(form.pd_industryOther || ''),
  ],
  websiteUrl: form.pd_websiteUrl || '',
  yearsExperience: form.pd_yearsExperience ? Number(form.pd_yearsExperience) : null,
  referralFee: form.pd_referralFee || '',
  promotions: form.pd_promotions || '',
  preferredContact: form.pd_preferredContact || '',
});

const PD_KEYS = ['aboutUs', 'pd_headline', 'pd_industry', 'pd_specialties', 'pd_servicesOffered', 'pd_servicesNeeded', 'pd_callMeWhen', 'pd_painPoint', 'pd_geography', 'pd_revenueMin', 'pd_revenueMax', 'pd_employeeCount', 'pd_seats', 'pd_businessType', 'pd_businessTypeOther', 'pd_industriesServed', 'pd_industryOther', 'pd_websiteUrl', 'pd_yearsExperience', 'pd_referralFee', 'pd_promotions', 'pd_preferredContact'];

const EditProfile = () => {
  const user = useSelector((state:any) => state.user);
  const { id: routeId } = useParams<{ id?: string }>();
  const targetId = routeId || user._id;
  const isOtherUser = !!routeId && routeId !== user._id;
  const dispatch=useDispatch()
  const [data, setData] = useState('');
  const [form, setForm]:any = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [images, setImages]:any = useState({ image: '' });
  const history=useNavigate()
  const [submitted, setSubmitted] = useState(false)
  // Spotlight bubbles its current value up here so the main Save persists it with the profile.
  const [spotlightData, setSpotlightData] = useState<any>(null)
  // cw: per-group profile (Option A, unified box). groupEditId '' = editing the default profile;
  // when a group is selected we edit THAT group's version of the SAME fields. defaultPdRef holds the
  // default field values so switching back to Default restores them.
  const [groupEditId, setGroupEditId] = useState('')
  const [groupList, setGroupList] = useState<{ id: string; name: string }[]>([])
  const defaultPdRef = useRef<any>(null)
  // cw: unsaved-changes guard — profile fields AND Spotlight edits count as unsaved changes, so
  // clicking away prompts save-or-discard (Spotlight is part of the profile).
  const isDirtyRef = useRef(false)
  const isNavigatingRef = useRef(false)
  const initialLoadDone = useRef(false)
  const handleSaveRef = useRef<null | (() => void)>(null)

  const gallaryData = () => {
    loader(true)
    setIsLoading(true)
    ApiClient.get(`api/user/detail`,{id:targetId}).then(res => {
      if (res.success) {
        let value = res.data
        let payload:any = {
          id: targetId,
          email: value.email || '',
          mobileNo: (() => {
            const raw = String(value.mobileNo || '').replace(/\D/g, '');
            if (!raw) return '';
            return raw.startsWith('1') ? raw : `1${raw}`;
          })(),
          firstName: value.firstName || '',
          lastName: value.lastName || '',
          multiAddress: value.multiAddress || [],
          profession: value.profession || '',
          company: value.company || '',
          companyUrl: value.companyUrl || '',
          address: value.address || '',
          address2: value.address2 || '',
          state: value.state || '',
          postal_code: value.postal_code || '',
          city: value.city || '',
          timezone: value.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles',
          secondaryEmail: value.secondaryEmail || '',
          adminComment: value.adminComment || '',
          linkedInUrl: value.linkedInUrl || '',
          certification: value.certification || '',
          skills: value.skills || [],
          networkingGroup: value.networkingGroup || '',
          category: value.category?._id || value.category || null,
          subCategory: value.subCategory?._id || value.subCategory || null,
          subSubCategory: value.subSubCategory?._id || value.subSubCategory || null,
          aboutUs: value.aboutUs || '',
          country: value.country || 'usa',
          pd_headline: value.profileData?.headline || '',
          pd_industry: value.profileData?.industry || '',
          pd_specialties: (value.profileData?.specialties || []).join(', '),
          pd_servicesOffered: (value.profileData?.servicesOffered || []).join(', '),
          pd_servicesNeeded: (value.profileData?.servicesNeeded || []).join(', '),
          pd_callMeWhen: value.profileData?.callMeWhen || '',
          pd_painPoint: value.profileData?.icp?.painPoint || '',
          pd_geography: value.profileData?.icp?.geography || '',
          pd_revenueMin: value.profileData?.icp?.revenueMin || '',
          pd_revenueMax: value.profileData?.icp?.revenueMax || '',
          pd_employeeCount: value.profileData?.icp?.employeeCount || '',
          pd_seats: value.profileData?.icp?.seats || '',
          pd_businessType: value.profileData?.businessType || '',
          pd_businessTypeOther: value.profileData?.businessTypeOther || '',
          pd_industriesServed: (value.profileData?.industriesServed || []).filter((x: string) => INDUSTRY_OPTIONS.includes(x)),
          pd_industryOther: (value.profileData?.industriesServed || []).filter((x: string) => !INDUSTRY_OPTIONS.includes(x)).join(', '),
          pd_websiteUrl: value.profileData?.websiteUrl || '',
          pd_yearsExperience: value.profileData?.yearsExperience || '',
          pd_referralFee: value.profileData?.referralFee || '',
          pd_promotions: value.profileData?.promotions || '',
          pd_preferredContact: value.profileData?.preferredContact || '',
        }
        setForm(payload)
        // cw: snapshot the default profile-detail fields + list the user's groups for the selector.
        const dp: any = {}; PD_KEYS.forEach(k => { dp[k] = (payload as any)[k]; }); defaultPdRef.current = dp;
        setGroupList((value.groups || [])
          .filter((g: any) => g && g.groupId && g.status !== 'removed')
          .map((g: any) => ({ id: (g.groupId._id || g.groupId).toString(), name: (g.groupId.name) || 'Group' })))
        // cw: default the "Editing profile for" selector to the member's CURRENT group
        // (lastUsedGroupId), so the profile reflects the group they're in. Load that group's saved
        // override if it has one; otherwise the default fields show. Falls back to Default.
        const currentGid = value.lastUsedGroupId ? (value.lastUsedGroupId._id || value.lastUsedGroupId).toString() : ''
        const inCurrent = (value.groups || []).some((g: any) => g && g.groupId && (g.groupId._id || g.groupId).toString() === currentGid && g.status !== 'removed')
        if (currentGid && inCurrent) {
          setGroupEditId(currentGid)
          ApiClient.get('api/user/group-profile', { userId: targetId, groupId: currentGid }).then((res: any) => {
            if (res.success && res.data && res.data.useCustomProfile && res.data.groupProfileData && Object.keys(res.data.groupProfileData).length) {
              const gpd = res.data.groupProfileData
              setForm((f: any) => ({ ...f, ...pdFieldsFromProfile(gpd, gpd.bio) }))
            }
          }).catch(() => {})
        } else {
          setGroupEditId('')
        }
        let img = images
        Object.keys(images).map(itm => {
          img[itm] = value[itm]
        })
        setImages({ ...img })
        setData(value)
      }
      loader(false)
      setIsLoading(false)
    })
  };

  const getError = (key:any) => {
    return formModel.getError('profileForm',key)
  }

  // cw: switch which group's profile the Profile Details fields are editing. '' = default.
  const pickGroup = (gid: string) => {
    // preserve any unsaved edits to the default before leaving Default mode
    if (groupEditId === '') {
      const dp: any = {}; PD_KEYS.forEach(k => { dp[k] = form?.[k]; }); defaultPdRef.current = dp;
    }
    setGroupEditId(gid)
    if (!gid) {
      // back to the default profile
      setForm((f: any) => ({ ...f, ...(defaultPdRef.current || {}) }))
      return
    }
    // selecting a group immediately edits that group's profile: load its saved override if any,
    // otherwise seed the fields from the default so they start from something.
    ApiClient.get('api/user/group-profile', { userId: targetId, groupId: gid }).then((res: any) => {
      if (res.success && res.data) {
        const gpd = res.data.groupProfileData || {}
        const src = (res.data.useCustomProfile && Object.keys(gpd).length)
          ? pdFieldsFromProfile(gpd, gpd.bio)
          : (defaultPdRef.current || {})
        setForm((f: any) => ({ ...f, ...src }))
      }
    }).catch(() => {})
    // cw: switching which group you're editing also makes it your CURRENT group (lastUsedGroupId),
    // so the rest of the app (header, Find Members, event context) follows. Own profile only.
    if (!isOtherUser) {
      ApiClient.post('api/user/switch-group', { groupId: gid }).then((res: any) => {
        if (res.success) {
          ApiClient.get('api/user/detail', { id: user._id }).then((ur: any) => {
            if (ur.success) dispatch(login_success({ ...user, ...ur.data }))
          }).catch(() => {})
        }
      }).catch(() => {})
    }
  }

  const handleSubmit = (e:any) => {
    e.preventDefault();
    setSubmitted(true)
    // let invalid = formModel.getFormError('profileForm')
    // if (invalid) return

    // cw: per-group save — when a group is selected, the Profile Details fields save to THAT group's
    // override (not the global profile). Identity/contact and Spotlight stay global (handled below).
    if (groupEditId) {
      const groupProfile = profileFromPdFields(form);
      loader(true)
      ApiClient.post('api/user/group-profile/save', {
        userId: targetId, groupId: groupEditId, useCustomProfile: true, profileData: groupProfile,
      }).then((res: any) => {
        loader(false)
        if (res.success) {
          isDirtyRef.current = false
          // Regenerate this group's search embedding so Find Members reflects the group profile.
          ApiClient.post('api/profile/generate-embedding', { userId: targetId, groupId: groupEditId }).catch(() => {})
          toast.success('Profile successfully saved')
        } else {
          toast.error(res.message || 'Save failed')
        }
      }).catch(() => { loader(false); toast.error('Save failed') })
      return;
    }

    const profileData = {
      ...(user.profileData || {}),
      headline: form.pd_headline || '',
      industry: form.pd_industry || '',
      specialties: form.pd_specialties ? form.pd_specialties.split(',').map((s: string) => s.trim()).filter(Boolean) : [],
      servicesOffered: form.pd_servicesOffered ? form.pd_servicesOffered.split(',').map((s: string) => s.trim()).filter(Boolean) : [],
      servicesNeeded: form.pd_servicesNeeded ? form.pd_servicesNeeded.split(',').map((s: string) => s.trim()).filter(Boolean) : [],
      callMeWhen: form.pd_callMeWhen || '',
      icp: {
        painPoint: form.pd_painPoint || '',
        geography: form.pd_geography || '',
        revenueMin: form.pd_revenueMin || '',
        revenueMax: form.pd_revenueMax || '',
        employeeCount: form.pd_employeeCount || '',
        seats: form.pd_seats || '',
      },
      businessType: form.pd_businessType || '',
      businessTypeOther: form.pd_businessType === 'Other' ? (form.pd_businessTypeOther || '') : '',
      industriesServed: [
        ...(form.pd_industriesServed || []),
        ...((form.pd_industryOther || '').split(',').map((s: string) => s.trim()).filter(Boolean)),
      ],
      websiteUrl: form.pd_websiteUrl || '',
      yearsExperience: form.pd_yearsExperience ? Number(form.pd_yearsExperience) : null,
      referralFee: form.pd_referralFee || '',
      promotions: form.pd_promotions || '',
      preferredContact: form.pd_preferredContact || '',
    };
    const { pd_headline, pd_industry, pd_specialties, pd_servicesOffered, pd_servicesNeeded, pd_callMeWhen, pd_painPoint, pd_geography, pd_revenueMin, pd_revenueMax, pd_employeeCount, pd_seats, pd_businessType, pd_businessTypeOther, pd_industriesServed, pd_industryOther, pd_websiteUrl, pd_yearsExperience, pd_referralFee, pd_promotions, pd_preferredContact, ...rest } = form;
    let value = { ...rest,
      id: targetId,
      addedBy: user._id,
      profileData,
      ...images
     }
   Object.keys(value).map(itm=>{
    if(!value[itm] && itm !== 'profileData') value[itm]=null
   })
   // Persist the Member Spotlight alongside the rest of the profile.
   if (spotlightData) value.spotlight = spotlightData

    loader(true)
    ApiClient.put('api/user', value).then(res => {
      if (res.success) {
        // Saved — clear the unsaved-changes guard so the post-save navigation doesn't re-prompt.
        isDirtyRef.current = false;
        isNavigatingRef.current = true;
        toast.success('Profile successfully saved');
        // Regenerate search embeddings so profile + Spotlight changes reach Find Members.
        // Embeddings are per (user, group), so regenerate for each of the user's active groups.
        (user?.groups || []).filter((g: any) => g.status === 'active').forEach((g: any) => {
          const gid = g.groupId?._id || g.groupId
          if (gid) ApiClient.post('api/profile/generate-embedding', { userId: targetId, groupId: gid }).catch(() => {})
        })
        if (!isOtherUser) {
          let uUser = { ...user, ...value }
          dispatch(login_success(uUser))
          history("/profile")
        } else {
          history(`/user-profile/${targetId}`, { replace: true })
        }
      }
      loader(false)
    })
  };

  const uploadImage = (e:any) => {
    setForm({ ...form, baseImg: e.target.value })
    let files = e.target.files
    let originalFile = files.item(0)
    const safeName = originalFile.name.replace(/[^a-zA-Z0-9_\-. ]/g, '')
    const file = new File([originalFile], safeName, { type: originalFile.type })
    loader(true)
    ApiClient.postFormData('api/upload/image?modelName=users', { file: file, modelName: 'users' }).then(res => {
      if (res.fileName) {
        let image = res.fileName
        setForm({ ...form, image: image, baseImg: '' })
      } else {
        setForm({ ...form, baseImg: '' })
      }
      loader(false)
    })
  }

 
  const imageResult = (e:any, key:any) => {
    images[key] = e.value
    setImages(images)

}


  useEffect(
    () => {
      if (user.loggedIn) {
        gallaryData();
      }
    },[]);

    // Keep a callable save reference for the unsaved-changes prompt.
    useEffect(() => {
      handleSaveRef.current = () => { handleSubmit({ preventDefault: () => {} } as any); };
    });

    // Once the profile has loaded, start treating changes as "unsaved".
    useEffect(() => {
      if (!isLoading && form) {
        const t = setTimeout(() => { initialLoadDone.current = true; }, 600);
        return () => clearTimeout(t);
      }
    }, [isLoading, form]);

    // Mark dirty on any profile-field, image, or Spotlight change after the initial load.
    useEffect(() => {
      if (initialLoadDone.current) isDirtyRef.current = true;
    }, [form, images, spotlightData]);

    // Warn on browser tab close/refresh with unsaved changes.
    useEffect(() => {
      const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        if (isDirtyRef.current && !isNavigatingRef.current) {
          e.preventDefault();
          e.returnValue = '';
        }
      };
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, []);

    // Intercept in-app link clicks while there are unsaved changes → save-or-discard prompt.
    useEffect(() => {
      const handleClick = (e: any) => {
        if (!isDirtyRef.current || isNavigatingRef.current) return;
        const link = e.target?.closest?.('a[href]');
        if (!link) return;
        const href = link.getAttribute('href');
        if (!href || href.startsWith('http') || href.startsWith('#')) return;
        e.preventDefault();
        e.stopPropagation();
        Swal.fire({
          title: 'Unsaved Changes',
          text: 'You have unsaved changes. Would you like to save before leaving?',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Save',
          cancelButtonText: 'Discard',
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
        }).then((result: any) => {
          isDirtyRef.current = false;
          isNavigatingRef.current = true;
          if (result.isConfirmed) {
            handleSaveRef.current?.(); // saves + navigates to the profile
          } else {
            window.location.href = href;
          }
        });
      };
      document.addEventListener('click', handleClick, true);
      return () => document.removeEventListener('click', handleClick, true);
    }, []);

    if (isLoading || !form) {
      return (
        <div className="text-center py-8">
          <img src="/assets/img/loader.gif" className="pageLoader" alt="Loading..." />
        </div>
      );
    }
  
    return (
      <>
       <Html
     handleSubmit={handleSubmit}
     setForm={setForm}
     imageResult={imageResult}
     images={images}
     form={form}
     uploadImage={uploadImage}
     getError={getError}
     submitted={submitted}
     groupEditId={groupEditId}
     groupList={groupList}
     pickGroup={pickGroup}
     isOtherUser={isOtherUser}
     />
     <div className="grid grid-cols-12 gap-4 mt-4">
       <div className="col-span-12">
         <Spotlight value={(data as any)?.spotlight} onChange={setSpotlightData} />
       </div>
     </div>
    </>
  );
};

export default EditProfile;
